// Client
import { getDirectoryClient } from "../../client";

// Constants
const HUBSPOT_IFRAME_CONTAINER_ID = "hubspot-messages-iframe-container";
const HUBSPOT_SHADOW_CONTAINER_SELECTOR = ".hs-shadow-container";
const twelveHoursMinusOneMinute = 1000 * 60 * 60 * 12 - 60_000; // 12h - 1min

// Custom types
interface ConversationsAPI {
  on: (event: "widgetLoaded" | "userInteractedWithWidget" | "widgetClosed", cb: () => void) => void;
  off: (
    event: "widgetLoaded" | "userInteractedWithWidget" | "widgetClosed",
    cb: () => void
  ) => void;
  widget: {
    status: () => { loaded: boolean; pending: boolean };
    load: () => void;
    open: () => void;
  };
  clear: (opts: { resetWidget: boolean }) => void;
}

declare global {
  interface Window {
    HubSpotConversations?: ConversationsAPI;
    hsConversationsSettings: {
      loadImmediately: boolean;
      identificationEmail?: string;
      identificationToken?: string;
    };
    hsConversationsOnReady: Array<() => void>;
  }
}

// HubSpot Chat Integration Class
export class HubSpotChatClass {
  private script_inserted: boolean = false;
  private widget_status: "not_loaded" | "loading" | "loaded" | "pending" = "not_loaded";
  private API: ConversationsAPI | null = null;
  private email: string | null = null;
  private tokenStatus: "ready" | "loading" | "error" = "ready";
  private token: string | null = null;

  constructor(email?: string) {
    if (email) {
      this.email = email;
    }
  }

  initialize(userEmail?: string): void {
    if (userEmail) {
      this.setEmail(userEmail);
    }
    if (this.script_inserted) {
      return;
    }

    insertHubspotChat(() => this.onConversationsAPIReady());
    this.script_inserted = true;
  }

  setEmail(email: string | null): void {
    if (email && email !== this.email) {
      this.email = email;
      this.retrieveHubspotIdToken();

      setTimeout(() => {
        this.retrieveHubspotIdToken();
      }, twelveHoursMinusOneMinute);
    }
  }

  getEmail(): string | null {
    return this.email;
  }

  isInserted(): boolean {
    return this.script_inserted;
  }

  private setToken(token: string): void {
    this.token = token;
    this.updateWindowSettings();
    this.tokenStatus = "ready";
  }

  private onConversationsAPIReady(): void {
    const winAPI = window.HubSpotConversations;
    if (winAPI !== this.API) {
      if (this.API) {
        this.API.off("widgetLoaded", this.onWidgetLoaded);
      }

      if (!winAPI) {
        return;
      }

      this.API = winAPI;
      this.API.on("widgetLoaded", this.onWidgetLoaded);
      this.loadHubspotWidget();
    }
  }

  private onWidgetLoaded = (): void => {
    // Add MutationObserver to detect class changes on .hs-shadow-container
    const container: HTMLElement | null = document.getElementById(HUBSPOT_IFRAME_CONTAINER_ID);
    // On first load, set data-initial
    if (container && !container.hasAttribute("data-initial")) {
      container.setAttribute("data-initial", "true");
    }
    if (container) {
      const shadow = container.querySelector<HTMLElement>(HUBSPOT_SHADOW_CONTAINER_SELECTOR);
      if (shadow) {
        // Set iframe class based on initial state
        // Set data-widget-display on the root container
        if (container) {
          if (shadow.classList.contains("active")) {
            container.setAttribute("data-widget-display", "open");
          }

          // Set up a MutationObserver for style changes on the container
          if ((container as any).__sjWidthObserver) {
            (container as any).__sjWidthObserver.disconnect();
          }
          const widthObserver = new MutationObserver(() => {
            const width = getComputedStyle(container).width;

            // Consider closed if width is less than a threshold
            if (parseFloat(width) < 101) {
              this.onWidgetClosed();
            }
          });
          widthObserver.observe(container, {
            attributes: true,
            attributeFilter: ["style", "class"],
          });
          (container as any).__sjWidthObserver = widthObserver;
        }
        // Disconnect any previous observer
        if ((shadow as any).__sjObserver) {
          (shadow as any).__sjObserver.disconnect();
        }
        const observer = new MutationObserver(() => {
          if (shadow.classList.contains("active")) {
            this.onWidgetOpened();
          } else {
            if (container.hasAttribute("data-initial")) {
              container.removeAttribute("data-initial");
            }
          }
        });
        observer.observe(shadow, { attributes: true, attributeFilter: ["class", "style"] });
        // Store observer for later cleanup
        (shadow as any).__sjObserver = observer;
      }
    }
    const previous = this.widget_status;
    this.widget_status = "loaded";

    if (previous === "pending") {
      this.updateWindowSettings();
      this.reloadHubspotWidget();
    }
  };

  // No longer needed: click handler replaced by MutationObserver

  private onWidgetOpened = (): void => {
    const container = document.getElementById(HUBSPOT_IFRAME_CONTAINER_ID);
    if (container) {
      container.setAttribute("data-widget-display", "open");
    }
  };

  private onWidgetClosed = (): void => {
    const container = document.getElementById(HUBSPOT_IFRAME_CONTAINER_ID);
    if (!container) return;

    container.setAttribute("data-widget-display", "closed");
  };

  private updateWindowSettings(): void {
    if (this.email && this.token) {
      if (this.shouldUpdateWindowSettings()) {
        window.hsConversationsSettings = {
          loadImmediately: false,
          identificationEmail: this.email,
          identificationToken: this.token,
        };
      }
    }
  }

  private shouldUpdateWindowSettings(): boolean {
    const { identificationEmail, identificationToken } = window.hsConversationsSettings ?? {};
    return identificationEmail !== this.email || identificationToken !== this.token;
  }

  private async retrieveHubspotIdToken(): Promise<void> {
    this.tokenStatus = "loading";
    const client = getDirectoryClient();
    const { data, error } = await client.hubspotId();
    if (error || !data || !data.token) {
      console.error("Error retrieving HubSpot ID token:", error || "no data");
      this.tokenStatus = "error";
      return;
    }
    this.setToken(data.token);
    this.loadHubspotWidget();
  }

  private loadHubspotWidget(): void {
    if (!this.API || this.tokenStatus !== "ready") return;

    const status = this.API.widget.status();

    if (!status.loaded && !status.pending) {
      this.widget_status = "loading";
      this.API.widget.load();
    }

    if (status.loaded) {
      this.reloadHubspotWidget();
    }

    if (!status.loaded && status.pending) {
      this.widget_status = "pending";
    }
  }

  private reloadHubspotWidget(): void {
    this.widget_status = "loading";
    this.API!.clear({ resetWidget: true });
  }
}

// standalone helper
function insertHubspotChat(onReady: () => void): void {
  console.log("Inserting HubSpot chat script");

  window.hsConversationsSettings = { loadImmediately: false };
  window.hsConversationsOnReady = [onReady];

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.id = "hs-script-loader";
  script.async = true;
  script.defer = true;
  script.src = "//js.hs-scripts.com/6720694.js";

  document.body.appendChild(script);
}
